require('../bootstrap');
import Vue from 'vue'
import axios from 'axios'
import {store} from '../store'
import 'vue2-datepicker/index.css';
import vueDebounce from 'vue-debounce'
import Toasted from 'vue-toasted';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { flare } from "@flareapp/js";
import { globalMixin } from './mixin'
import { createAxios } from './axios'

window.Vue = Vue;

/*
 * The <font-awesome-icon> shell stays registered globally because it is cheap,
 * but the solid icon pack is NOT loaded here.
 *
 * `library.add(fas)` used to live in this module. Every entry bundle imports
 * createApp, so the whole pack — 1,012 KB of source, 27% of vendor.js — was
 * downloaded by every visitor on every page. Only two components actually render
 * FA icons (UniversityDetailPage and LanguageSchoolDetailPage, for API-supplied
 * facility icons) and both are already async-imported, so each registers the
 * pack itself and it now travels in their own chunk.
 */

// Only enable Flare in production, we don't want to waste your quota while you're developing:
if (process.env.NODE_ENV === 'production') {
	flare.light();
}

Vue.use(Toasted)
Vue.use(vueDebounce)
Vue.component('font-awesome-icon', FontAwesomeIcon)
Vue.mixin(globalMixin)

// All entry bundles share one webpack runtime (mix.extract()), so this module
// is a page-wide singleton: every bundle registers its component map here and
// the page is mounted exactly once, with the union of all registered maps.
//
// All entries are loaded with `defer`, so every registerVueBundle() call runs
// before DOMContentLoaded — no bundle can miss the mount.
const registry = {
	components: {},
	onToken: [],
	mountOn: 'interactive',
	started: false,
}
// Debug handle: lets us confirm in the browser which bundles registered.
window.__EA_VUE_REGISTRY__ = registry

function waitForLocalStorage(key, cb, timer) {
	// NOTE: use === null (key not yet written) rather than a falsy check.
	// For guests session('api-token') is empty, so app.blade.php writes an
	// empty string. Treating "" as "missing" would poll forever and never
	// mount, leaving every Vue component unmounted on the page.
	if ( localStorage.getItem( key ) === null ) {
		return timer = setTimeout(
			waitForLocalStorage.bind( null, key, cb ),
			100
		)
	}
	clearTimeout( timer )
	if ( typeof cb !== 'function' ) {
		return localStorage.getItem( key )
	}
	return cb( localStorage.getItem( key ) )
}

/**
 * Take down a server-rendered .vue-placeholder once its Vue root has actually
 * rendered something.
 *
 * This used to run unconditionally in the root's mounted() hook. Most bundles
 * register their components as async chunks, so at that point the subtree is
 * still the comment node Vue renders for an unresolved async component and the
 * chunk has only just been requested — the placeholder was removed with nothing
 * to take its place. On the home page that left the search panel a blank 398px
 * box for 551ms (measured, emulated Moto G Power over Slow 4G), which is the
 * exact gap the placeholder is there to cover.
 *
 * Every placeholder is positioned so the real component lands on top of it, so
 * holding one a few frames too long is invisible. Removing it too early is not.
 *
 * MutationObserver callbacks are microtasks, so the placeholder is hidden in the
 * same checkpoint the component is inserted — before the next paint, with no
 * frame showing both.
 */
function hidePlaceholderWhenRendered(root, placeholder) {
	// Element children, not height: a component can legitimately render
	// something with no box, and reading height would force a layout here.
	const hasRendered = () => root.children.length > 0
	const hide = () => { placeholder.style.display = 'none' }

	if (hasRendered()) return hide()

	let failsafe
	const observer = new MutationObserver(() => {
		if (!hasRendered()) return
		observer.disconnect()
		clearTimeout(failsafe)
		hide()
	})
	observer.observe(root, { childList: true })

	// A chunk that fails to load, or a root that only ever renders a comment
	// (a modal-only app), would otherwise strand the placeholder on the page.
	failsafe = setTimeout(() => {
		observer.disconnect()
		hide()
	}, 10000)
}

function mountVueApps() {
	document.querySelectorAll('.vue-app').forEach((el, index) => {
		if (el.dataset.vueMounted) return
		el.dataset.vueMounted = 'true'

		el.classList.add(`vue-app-${index}`)
		new Vue({
			el: `.vue-app-${index}`,
			components: {...registry.components},
			store,
			axios,
			mounted() {
				const prev = this.$el.previousElementSibling;
				if (prev && prev.classList.contains('vue-placeholder')) {
					hidePlaceholderWhenRendered(this.$el, prev);
				}
			}
		});
	})
}

function start() {
	if (registry.started) return
	registry.started = true

	// Don't boot synchronously: when the token is already in localStorage the
	// waitForLocalStorage callback fires immediately, which could mount the
	// page before the other defer'd entry bundles have registered. Defer
	// scripts run before DOMContentLoaded, and a timer can fire in between
	// them — so wait for DOMContentLoaded itself, which is only already past
	// when the document is 'complete' (a bundle injected after page load).
	if (document.readyState === 'complete') {
		setTimeout(boot, 0)
	} else {
		document.addEventListener('DOMContentLoaded', boot)
	}
}

function boot() {
	waitForLocalStorage('api-token', (apiToken) => {
		Vue.prototype.$axios = createAxios(apiToken)
		store.$axios = Vue.prototype.$axios
		store.dispatch('global/getInit')

		// Before mounting: components may use window.Echo as soon as they are created.
		registry.onToken.forEach((cb) => cb(apiToken))

		if (registry.mountOn === 'load') {
			if (document.readyState === 'complete') {
				mountVueApps()
			} else {
				window.addEventListener('load', mountVueApps)
			}
		} else {
			if (document.readyState === 'complete' || document.readyState === 'interactive') {
				mountVueApps()
			} else {
				document.addEventListener('DOMContentLoaded', mountVueApps)
			}
		}

		return apiToken
	})
}

/**
 * Register a bundle's components for the current page.
 *
 * @param {Object}   options.components  kebab-tag -> async component map
 * @param {string}   options.mountOn     'interactive' (default) or 'load'
 * @param {Function} options.onToken     called once with the api token, before mounting
 */
export function registerVueBundle({ components = {}, mountOn = null, onToken = null }) {
	Object.assign(registry.components, components)
	if (mountOn) registry.mountOn = mountOn
	if (typeof onToken === 'function') registry.onToken.push(onToken)
	start()
}
